import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import { Brain, Target, TrendingUp, Zap } from 'lucide-react'

const modelPerformanceData = [
  { week: 'Week 1', accuracy: 87.2, precision: 85.1, recall: 89.3, f1Score: 87.1 },
  { week: 'Week 2', accuracy: 91.4, precision: 89.7, recall: 93.1, f1Score: 91.3 },
  { week: 'Week 3', accuracy: 93.8, precision: 92.4, recall: 95.2, f1Score: 93.8 },
  { week: 'Week 4', accuracy: 95.3, precision: 94.1, recall: 96.5, f1Score: 95.3 }
]

const confusionMatrixData = {
  truePositive: 892,
  falsePositive: 34,
  trueNegative: 1823,
  falseNegative: 67
}

const featureImportance = [
  { feature: 'Sequential Phone Pattern', importance: 0.28, description: 'Phone number sequences' },
  { feature: 'IP Data Center Score', importance: 0.24, description: 'IP reputation analysis' },
  { feature: 'Email Domain Risk', importance: 0.18, description: 'Domain trustworthiness' },
  { feature: 'Zip Code Concentration', importance: 0.15, description: 'Geographic clustering' },
  { feature: 'Submission Velocity', importance: 0.12, description: 'Time-based patterns' },
  { feature: 'Form Interaction Time', importance: 0.03, description: 'User behavior analysis' }
]

const modelVersions = [
  { version: 'v1.0', accuracy: 87.2, deployDate: '2024-01-15', status: 'retired' },
  { version: 'v1.1', accuracy: 91.4, deployDate: '2024-02-01', status: 'retired' },
  { version: 'v1.2', accuracy: 93.8, deployDate: '2024-02-15', status: 'retired' },
  { version: 'v2.0', accuracy: 95.3, deployDate: '2024-03-01', status: 'active' },
  { version: 'v2.1', accuracy: 96.1, deployDate: '2024-03-15', status: 'testing' }
]

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-semibold text-foreground">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.dataKey}: {entry.value}%
          </p>
        ))}
      </div>
    )
  }
  return null
}

const ConfusionMatrix = () => {
  const { truePositive, falsePositive, trueNegative, falseNegative } = confusionMatrixData
  const total = truePositive + falsePositive + trueNegative + falseNegative
  
  const precision = (truePositive / (truePositive + falsePositive) * 100).toFixed(1)
  const recall = (truePositive / (truePositive + falseNegative) * 100).toFixed(1)
  const f1Score = (2 * (parseFloat(precision) * parseFloat(recall)) / (parseFloat(precision) + parseFloat(recall))).toFixed(1)
  const accuracy = ((truePositive + trueNegative) / total * 100).toFixed(1)
  
  return (
    <div className="space-y-4">
      <h4 className="font-semibold">Confusion Matrix</h4>
      
      {/* Matrix Grid */}
      <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto">
        <div></div>
        <div className="text-center text-sm font-medium text-muted-foreground">Predicted</div>
        <div></div>
        
        <div className="text-center text-sm font-medium text-muted-foreground self-center">Actual</div>
        <div className="grid grid-cols-2 gap-1">
          <div className="text-xs text-center font-medium text-muted-foreground">Fraud</div>
          <div className="text-xs text-center font-medium text-muted-foreground">Legit</div>
        </div>
        <div></div>
        
        <div className="grid grid-rows-2 gap-1">
          <div className="text-xs text-center font-medium text-muted-foreground">Fraud</div>
          <div className="text-xs text-center font-medium text-muted-foreground">Legit</div>
        </div>
        <div className="grid grid-cols-2 grid-rows-2 gap-1">
          {/* True Positive */}
          <div className="bg-green-100 dark:bg-green-900/30 border-2 border-green-300 dark:border-green-700 p-3 rounded text-center">
            <div className="text-lg font-bold text-green-800 dark:text-green-200">{truePositive}</div>
            <div className="text-xs text-green-600 dark:text-green-400">TP</div>
          </div>
          {/* False Negative */}
          <div className="bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 p-3 rounded text-center">
            <div className="text-lg font-bold text-red-800 dark:text-red-200">{falseNegative}</div>
            <div className="text-xs text-red-600 dark:text-red-400">FN</div>
          </div>
          {/* False Positive */}
          <div className="bg-yellow-100 dark:bg-yellow-900/30 border-2 border-yellow-300 dark:border-yellow-700 p-3 rounded text-center">
            <div className="text-lg font-bold text-yellow-800 dark:text-yellow-200">{falsePositive}</div>
            <div className="text-xs text-yellow-600 dark:text-yellow-400">FP</div>
          </div>
          {/* True Negative */}
          <div className="bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-300 dark:border-blue-700 p-3 rounded text-center">
            <div className="text-lg font-bold text-blue-800 dark:text-blue-200">{trueNegative}</div>
            <div className="text-xs text-blue-600 dark:text-blue-400">TN</div>
          </div>
        </div>
        <div></div>
      </div>
      
      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="text-center p-3 bg-muted/30 rounded">
          <div className="text-lg font-bold text-foreground">{precision}%</div>
          <div className="text-xs text-muted-foreground">Precision</div>
        </div>
        <div className="text-center p-3 bg-muted/30 rounded">
          <div className="text-lg font-bold text-foreground">{recall}%</div>
          <div className="text-xs text-muted-foreground">Recall</div>
        </div>
        <div className="text-center p-3 bg-muted/30 rounded">
          <div className="text-lg font-bold text-foreground">{f1Score}%</div>
          <div className="text-xs text-muted-foreground">F1 Score</div>
        </div>
        <div className="text-center p-3 bg-muted/30 rounded">
          <div className="text-lg font-bold text-foreground">{accuracy}%</div>
          <div className="text-xs text-muted-foreground">Accuracy</div>
        </div>
      </div>
    </div>
  )
}

export const MLPerformance: React.FC = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          ML Model Performance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Performance Trends */}
        <div>
          <h4 className="font-semibold mb-3">Learning Curve</h4>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={modelPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis domain={[80, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="accuracy" stroke="#3B82F6" strokeWidth={2} name="Accuracy" />
                <Line type="monotone" dataKey="precision" stroke="#10B981" strokeWidth={2} name="Precision" />
                <Line type="monotone" dataKey="recall" stroke="#F59E0B" strokeWidth={2} name="Recall" />
                <Line type="monotone" dataKey="f1Score" stroke="#8B5CF6" strokeWidth={2} name="F1 Score" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Confusion Matrix */}
        <ConfusionMatrix />

        {/* Feature Importance */}
        <div>
          <h4 className="font-semibold mb-3">Feature Importance</h4>
          <div className="space-y-2">
            {featureImportance.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{feature.feature}</span>
                    <span className="text-sm font-semibold">{(feature.importance * 100).toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-500"
                      style={{ width: `${feature.importance * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Model Versions */}
        <div>
          <h4 className="font-semibold mb-3">Model Version History</h4>
          <div className="space-y-2">
            {modelVersions.map((version, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded">
                <div className="flex items-center gap-3">
                  <Badge 
                    variant={version.status === 'active' ? 'default' : 
                             version.status === 'testing' ? 'secondary' : 'outline'}
                  >
                    {version.version}
                  </Badge>
                  <span className="text-sm">{version.deployDate}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{version.accuracy}%</span>
                  <Badge variant={
                    version.status === 'active' ? 'default' :
                    version.status === 'testing' ? 'secondary' : 'outline'
                  }>
                    {version.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}